import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { SETTINGS } from '../configs/common.settings';
import {
  getStoredPermissionOverrides,
  PERMISSION,
  resolveRolePermissions,
  ROLES,
  SUPERVISOR_LOCKED_PERMISSIONS,
} from '../configs/permissions.config';
import { getItem } from '../utils/safe-storage';
import { WebviewService } from './webview.service';
import { RoleStorageService } from './role-storage.service';
import { PermissionAccessService } from './permission-access.service';
import { RevampService } from './revamp.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private overrides: Record<string, string[]> | null = null;
  /** Server-resolved effective actions for current user (when loaded). */
  private effectiveActions: string[] | null = null;
  private useServerEffective = false;
  private readonly permissionsChangedSubject = new Subject<void>();
  readonly permissionsChanged$ = this.permissionsChangedSubject.asObservable();

  constructor(
    private webviewService: WebviewService,
    private roleStorage: RoleStorageService,
    private permissionAccess: PermissionAccessService,
    private revampService: RevampService
  ) {
    this.overrides = getStoredPermissionOverrides();
  }

  getRole(): string {
    return this.roleStorage.getRole();
  }

  reloadOverrides(): void {
    this.overrides = getStoredPermissionOverrides();
    this.notifyPermissionsChanged();
  }

  /** Load effective permissions from server for active company (when serverPermissions flag is on). */
  loadEffectiveFromServer(companyId?: string | null): void {
    // Gate: skip server resolution when revamp flag is off
    if (!this.revampService.isEnabled('serverPermissions')) {
      this.useServerEffective = false;
      this.effectiveActions = null;
      this.notifyPermissionsChanged();
      return;
    }
    const userId = getItem(SETTINGS.LOGGED_IN_USER_ID) || '';
    if (!userId) {
      this.useServerEffective = false;
      this.effectiveActions = null;
      this.notifyPermissionsChanged();
      return;
    }
    const company =
      companyId !== undefined && companyId !== null
        ? companyId || undefined
        : getItem(SETTINGS.COMPANY) || undefined;
    this.permissionAccess
      .effective({
        userId,
        companyId: company || undefined,
      })
      .subscribe({
        next: res => {
          this.effectiveActions = res?.actions || [];
          this.useServerEffective = true;
          this.notifyPermissionsChanged();
        },
        error: () => {
          this.useServerEffective = false;
          this.effectiveActions = null;
          this.notifyPermissionsChanged();
        },
      });
  }

  clearServerEffective(): void {
    this.useServerEffective = false;
    this.effectiveActions = null;
    this.notifyPermissionsChanged();
  }

  notifyPermissionsChanged(): void {
    this.permissionsChangedSubject.next();
  }

  getCurrentRolePermissions(): string[] {
    if (this.useServerEffective && this.effectiveActions) {
      return this.effectiveActions;
    }
    const role = this.getRole();
    if (!role) return [];
    return resolveRolePermissions(role, this.overrides);
  }

  hasPermission(permissionCode: string): boolean {
    if (!permissionCode) return false;
    const role = this.getRole();
    if (!role) return false;
    if (role === ROLES.ADMIN) return true;
    // Server-resolved actions already include supervisor defaults minus any cut-offs.
    if (
      !this.isServerResolved() &&
      role === ROLES.SUPERVISOR &&
      SUPERVISOR_LOCKED_PERMISSIONS.includes(permissionCode)
    ) {
      return true;
    }
    const perms = this.getCurrentRolePermissions();
    return perms.includes(permissionCode);
  }

  isServerResolved(): boolean {
    return this.useServerEffective && !!this.effectiveActions;
  }

  hasAnyPermission(permissionCodes: string[]): boolean {
    if (!permissionCodes?.length) return false;
    return permissionCodes.some(code => this.hasPermission(code));
  }

  hasAllPermissions(permissionCodes: string[]): boolean {
    if (!permissionCodes?.length) return true;
    return permissionCodes.every(code => this.hasPermission(code));
  }

  canViewJobSingle(): boolean {
    if (this.webviewService.isWebview) {
      return true;
    }
    const role = this.getRole();
    if (
      role === ROLES.ADMIN ||
      (role === ROLES.SUPERVISOR && !this.isServerResolved())
    ) {
      return true;
    }
    if (this.hasPermission(PERMISSION.JOB_VIEW_LIST)) {
      return true;
    }
    return this.hasAnyPermission([
      PERMISSION.JOB_VIEW_SINGLE,
      PERMISSION.JOB_EDIT_FULL,
    ]);
  }

  isAdminOrManager(): boolean {
    return this.getRole() === ROLES.ADMIN;
  }
}
